import { brandHex } from '../../hooks/useBranding.jsx'
import { themeFor } from '../platform/dashboardTheme.js'

/**
 * A company's pages in ONE colour: theirs.
 *
 * The colour their administrator chose in My Organisation ("Main colour") is already the colour of the
 * selected item in the menu. The front page's banner, its number cards and its chart take the same one,
 * so the menu and the page read as one design. A company that has chosen none gets ProTrack's own blue,
 * which is the menu's own selected colour then -- still one colour throughout.
 *
 * The shape is the partner dashboard's (dashboardTheme.js): the same banner gradient, the same cards and
 * charts, in the company's colour instead of the product's.
 */

// ProTrack's own: the blue of a selected menu item when no company colour is set.
const OUR_BLUE = '#2563EB'

const channels = (hex) => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16))
const toHex = (rgb) => `#${rgb.map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`

function luminance(hex) {
  const lin = (c) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4) }
  const [r, g, b] = channels(hex)
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

/** The colour, darkened just enough for white writing on it to read (4.5:1). A strong colour is left as
 *  it is; a pale one -- a pastel a company is free to choose -- is deepened in its own hue. */
function forWhiteText(hex) {
  let rgb = channels(hex)
  for (let n = 0; n < 16 && luminance(toHex(rgb)) > 0.18; n++) rgb = rgb.map(v => v * 0.88)
  return toHex(rgb)
}

const shade = (hex, k) => toHex(channels(hex).map(v => v * k))
const wash = (hex, a) => toHex(channels(hex).map(v => v + (255 - v) * (1 - a)))

export function companyTheme(primaryColour) {
  const base = forWhiteText(brandHex(primaryColour) || OUR_BLUE)
  const neutral = themeFor()
  return {
    ...neutral,
    name: 'company',
    accent: base,
    accentDark: shade(base, 0.82),
    accentSoft: wash(base, 0.1),
    // One series on these pages, so the chart's first colour is the company's.
    series: [base, ...neutral.series.slice(1)],
    hero: `linear-gradient(120deg,${base} 0%,${shade(base, 0.82)} 48%,${shade(base, 0.52)} 100%)`,
    tint: `linear-gradient(135deg,${wash(base, 0.1)} 0%,#ffffff 100%)`,
  }
}
